import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import axiosAuth from "../axiosAuth";
import { CREATE_PROFILE, UPDATE_PROFILE } from "./apiRoutes";
import { removeEmptyFields } from "@/lib/utils";

export interface CreateEmployeePayload {
  name: string;
  email: string;
  mobile_number?: string;
  role: [];
  city: string;
  state: string;
  geographical_region_id: string;
}

export interface UpdateEmployeePayload extends Omit<CreateEmployeePayload, 'email'> {
  id: string;
}

interface UseCreateEmployeeOptions {
  onSuccess?: () => void;
}

interface UseUpdateEmployeeOptions {
  onSuccess?: () => void;
}

const useCreateEmployeeQuery = (options?: UseCreateEmployeeOptions) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateEmployeePayload) => {
      const response = await axiosAuth.post(CREATE_PROFILE, removeEmptyFields(data));
      return response.data;
    },
    onSuccess: () => {
      toast.success("Employee created successfully!");
      // Invalidate all employees queries to refresh the table regardless of filters
      queryClient.invalidateQueries({ queryKey: ["employees"] });
      // Call the custom onSuccess callback if provided
      options?.onSuccess?.();
    },
    onError: (error: any) => {
      const message = error?.response?.data?.issues?.message || "Failed to create employee";
      toast.error(message);
    },
  });
};

const useUpdateEmployeeQuery = (options?: UseUpdateEmployeeOptions) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateEmployeePayload) => {
      const { id, ...payload } = data;
      const response = await axiosAuth.put(`${UPDATE_PROFILE}${id}/`, removeEmptyFields(payload));
      return response.data;
    },
    onSuccess: () => {
      toast.success("Employee updated successfully!");
      // Invalidate all employees queries to refresh the table regardless of filters
      queryClient.invalidateQueries({ queryKey: ["employees"] });
      // Call the custom onSuccess callback if provided
      options?.onSuccess?.();
    },
    onError: (error: any) => {
      const message = error?.response?.data?.issues?.message || "Failed to update employee";
      toast.error(message);
    },
  });
};

export default useCreateEmployeeQuery;
export { useUpdateEmployeeQuery };